import { useState, useCallback, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, Clock } from 'react-feather';
import { useQuery } from '@tanstack/react-query';
import type { MessageGenSessionSummary } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { PhaseBadge } from './badges/PhaseBadge';
import { TypeBadge } from './badges/TypeBadge';

interface SessionHistoryDropdownProps {
  currentSessionId?: number;
  onSelectSession: (sessionId: number) => void;
  iconOnly?: boolean;
}

export function SessionHistoryDropdown({
  currentSessionId,
  onSelectSession,
  iconOnly = false,
}: SessionHistoryDropdownProps): React.JSX.Element {
  const apiClient = useChatApiClient();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['message-gen-sessions'],
    queryFn: () => apiClient.messageGeneration.listSessions(10),
    staleTime: 30000, // 30 seconds
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelectSession = useCallback(
    (sessionId: number) => {
      setIsOpen(false);
      onSelectSession(sessionId);
    },
    [onSelectSession]
  );

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${month}/${day} ${displayHours}:${minutes} ${ampm}`;
  };


  return (
    <Container ref={dropdownRef}>
      <DropdownButton onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen} $iconOnly={iconOnly}>
        <Clock size={16} />
        {!iconOnly && <span>Session History</span>}
        <ChevronDown size={16} />
      </DropdownButton>

      {isOpen && (
        <DropdownMenu>
          {isLoading && (
            <LoadingState>Loading sessions...</LoadingState>
          )}

          {!isLoading && sessions.length === 0 && (
            <EmptyState>No previous sessions</EmptyState>
          )}

          {!isLoading && sessions.length > 0 && (
            <SessionsList>
              {sessions.map((session) => {
                const isSegment = session.segmentIds && session.segmentIds.length === 1;
                const type = isSegment ? 'segment' : 'run';

                return (
                  <SessionCard
                    key={session.id}
                    onClick={() => handleSelectSession(session.id)}
                    $isCurrent={session.id === currentSessionId}
                  >
                    <SessionInfo>
                      <SessionName>
                        {session.name || 'Untitled Session'}
                        {session.id === currentSessionId && <CurrentBadge>Current</CurrentBadge>}
                      </SessionName>
                      <BadgesRow>
                        <DateTimeBadge>{formatDateTime(session.createdOn)}</DateTimeBadge>
                        {session.phase && <PhaseBadge phase={session.phase} />}
                        <BrandItem>Elderberry</BrandItem>
                        <ObjectiveItem>Safety</ObjectiveItem>
                      </BadgesRow>
                    </SessionInfo>
                  </SessionCard>
                );
              })}
            </SessionsList>
          )}
        </DropdownMenu>
      )}
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button<{ $isOpen: boolean; $iconOnly: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$iconOnly ? '4px' : '8px')};
  padding: ${(props) => (props.$iconOnly ? '8px' : '8px 16px')};
  background: white;
  border: 1px solid ${(props) => (props.$isOpen ? '#1271c4' : '#e5e5e5')};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #323232;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #1271c4;
    background: #f9f9f9;
  }

  svg {
    flex-shrink: 0;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 400px;
  max-width: 500px;
  max-height: 500px;
  overflow-y: auto;
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
`;

const LoadingState = styled.div`
  padding: 24px;
  text-align: center;
  color: #626262;
  font-size: 14px;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: #ADADAD;
  font-size: 14px;
`;

const SessionsList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
`;

const SessionCard = styled.button<{ $isCurrent: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  background: ${(props) => (props.$isCurrent ? '#f0f9ff' : 'white')};
  border: 1px solid ${(props) => (props.$isCurrent ? '#1271c4' : 'transparent')};
  border-radius: 8px;
  cursor: ${(props) => (props.$isCurrent ? 'default' : 'pointer')};
  transition: all 0.15s;
  text-align: left;

  &:hover {
    background: ${(props) => (props.$isCurrent ? '#f0f9ff' : '#f9f9f9')};
    border-color: ${(props) => (props.$isCurrent ? '#1271c4' : '#e5e5e5')};
  }

  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;

const SessionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SessionName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CurrentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #1271c4;
  color: white;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
`;

const BadgesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const BrandItem = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: #d1fae5;
  border: 1px solid #a7f3d0;
  color: #065f46;
`;

const ObjectiveItem = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: #e9d5ff;
  border: 1px solid #d8b4fe;
  color: #6b21a8;
`;

const DateTimeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: #e0e7ff;
  color: #4338ca;
`;
